import React, { SyntheticEvent, useRef } from 'react';
import styled from '@emotion/styled';
import { Carousel } from '~/shared/components/Carousel/Carousel';
import Grid from '~/shared/components/Grid/Grid';
import Chevron from '~/icons/chevron.svg';
import { breakpoints } from '~/theme/breakpoints';
import { Gutter } from '~/shared/components/Gutter/Gutter';
import { StyledResetButton } from '~/shared/components/Button/styled';
import { ModuleContainer } from '~/templates/blocks/components/ModuleContainer';
import { Link as LinkType } from '~/lib';
import { Link, Text } from '~/shared/components';
import { useRouter } from 'next/router';

export type TArticlesSlider = {
    categories?: LinkType[];
    onClick?: (e: React.SyntheticEvent, value: string) => void;
};

export type TCategoryPickerItem = {
    isActive?: boolean;
    title?: string;
    url?: string;
    isFirst?: boolean;
    onClick?: null | ((e: SyntheticEvent, value: string) => void);
};

const Icon = styled(Chevron)``;

const CarouselButton = styled(StyledResetButton)<{ position: 'left' | 'right' }>(
    ({ theme, position }) => ({
        backgroundColor: theme.colors.white,
        display: 'flex',
        alignItems: 'center',
        borderRadius: 24,
        padding: '7px 16px',
        position: 'relative',
        '&:disabled': {
            opacity: 0,
            zIndex: -1,
        },
        [`${Icon}`]: {
            transform: `rotate(${position === 'left' ? '180deg' : '0deg'})`,
        },
        zIndex: 2,
        boxShadow: '0 0px 10px rgba(0,0,0,0.3)',
    }),
);

const CarouselWrapper = styled.div({
    [breakpoints.sm]: {
        clipPath: 'inset(0 0px round 25px 25px 25px 25px)',
    },
    display: 'grid',
});

const SliderWrapper = styled.div({
    display: 'grid',
    gridArea: '1/1',
    '& > *': {
        gridArea: '1/1',
    },
});

const AbsoluteWrapper = styled.div(({ theme }) => ({
    position: 'absolute',
    bottom: 0,
    [breakpoints.sm]: {
        bottom: theme.space[6],
    },
}));

export const CategoryPickerItem = ({
    isFirst,
    isActive,
    url,
    onClick,
    title,
}: TCategoryPickerItem) => {
    return (
        <Wrapper
            isFirst={isFirst}
            href={url}
            isActive={isActive}
            onClick={(e) => onClick && title && onClick(e, title)}
        >
            <Text variant="bodySmall">{title}</Text>
        </Wrapper>
    );
};

export const CategorySlider = ({ categories, onClick }: TArticlesSlider) => {
    const nextRef = useRef<HTMLButtonElement | null>(null);
    const prevRef = useRef<HTMLButtonElement | null>(null);
    const { asPath } = useRouter();

    const ArticleItems = categories?.map((category, index) => (
        <CategoryPickerItem
            key={`${category.url}-${index}`}
            onClick={onClick}
            {...category}
            isFirst={index === 0}
            isActive={category.url === asPath}
        />
    ));

    return (
        <SliderWrapper>
            <CarouselWrapper>
                <Carousel
                    options={{ spaceBetween: 8 }}
                    items={ArticleItems}
                    nextRef={nextRef}
                    prevRef={prevRef}
                />
            </CarouselWrapper>

            <Grid flow="column" columns="auto 1fr auto">
                <CarouselButton aria-label="Previous" position="left" ref={prevRef}>
                    <Icon />
                </CarouselButton>

                <div />

                <CarouselButton aria-label="Next" position="right" ref={nextRef}>
                    <Icon />
                </CarouselButton>
            </Grid>
        </SliderWrapper>
    );
};

type Categories = {
    categories?: LinkType[];
};

const ArticlesMenu = ({ categories }: Categories) => {
    return (
        <AbsoluteWrapper>
            <ModuleContainer spacingBottom="xs" spacingTop="xs">
                <Gutter gutterSize="xl" gutterOnMobile={false}>
                    <CategorySlider categories={categories} />
                </Gutter>
            </ModuleContainer>
        </AbsoluteWrapper>
    );
};

export const Wrapper = styled(Link)<Pick<TCategoryPickerItem, 'isActive' | 'isFirst' | 'onClick'>>(
    ({ theme, isActive, isFirst }) => ({
        backgroundColor: isActive ? theme.colors.black : theme.colors.white,
        display: 'flex',
        alignItems: 'center',
        borderRadius: 24,
        padding: '0px 16px',
        minHeight: '38px',
        marginLeft: isFirst ? theme.space[3] : 0,
        transition: 'all .125s ease-in',
        transitionProperty: 'background-color, border-color',
        border: `1px solid`,
        color: theme.colors.black,
        borderColor: isActive ? theme.colors.black : theme.colors.white,
        [`${Text}`]: {
            color: isActive ? theme.colors.white : theme.colors.black,
            transition: 'color 0.125s ease-in',
            fontWeight: isActive ? theme.fontWeights.medium : theme.fontWeights.light,
        },

        '&:hover, &:focus': {
            backgroundColor: theme.colors.grey10,
            textDecoration: 'none',
        },

        [breakpoints.md]: {
            marginLeft: 0,
        },
    }),
);

export { ArticlesMenu };
